import { defineStore } from 'pinia';
import type { Ref } from 'vue';
import { ref } from 'vue';

import {
  ClientResourceContainer,
  LegacyClientResourceContainerQuoteStructure,
  transformEntitiesToComparableStructure,
  useClientStore,
} from '@/features/Clients';
import type { TariffFeatures } from '@/features/Features';
import { useFeaturesStore } from '@/features/Features';

import { useGlobalStore } from '@/features/Global/store';
import { useProfileStore } from '@/features/Profile';
import {
  hasCompanyQuestionInputData,
  hasContractInputData,
  InventoryContract,
  isLegacyInventoryContractContent,
  LegacyQuoteContent,
  LegacyQuoteVersion,
  Quote,
  QUOTE_RESET_AFTER_LOAD,
  QUOTE_TYPE,
  QuoteContent,
  QuoteVersion,
  VersicherungsangabenDirtyFields,
} from '@/features/Quotes/types';
import {
  buildMergedInputData,
  removeNullAndUndefinedValues,
  transferContractDataToContractStore,
  transferQuoteAllTimeTariffsDataToInputStore,
  transferQuoteDataToInputStore,
  transformClientEntitiesToEntityManagerStructure,
  transformInventoryContractToCurrentVersion,
  transformLegacyQuoteToCurrentVersion,
} from '@/features/Quotes/utils';
import { useContractStore } from '@/features/Sparten';
import { WEBREPORT_TYPE } from '@/features/Sparten/types';
import { createDefaultSortingState } from '@/features/Tariffs/sorting';
import { useTariffStore } from '@/features/Tariffs/store';
import { useApi } from '@/services/api/useApi';
import { VGL_ERROR_CODE } from '@/services/errorHandler/errorCodes';
import { getErrorMessage } from '@/services/errorHandler/utils';
import { VglError } from '@/services/errorHandler/vglError';
import type { ResourceContainer } from '@/types';
import hashCode from '@/utils/hashCode/hashCode';
import { useStorableState } from '@/utils/useStorableState/useStorableState';
import { useCompaniesStore } from '@/features/Companies/store';
import { useCompanyQuestionStore } from '@/features/CompanyQuestions/store';
import { useBrokerStore } from '@/features/Broker/store';
import { settingsSchema } from '@/features/Broker/settingsSchema';
import type { SearchDataComparable } from '@/features/Global/types';
import { getCurrentSpartenConfig } from '@/utils/getCurrentSpartenConfig/getCurrentSpartenConfig';
import { SECTION } from '@/components/layout/types';

interface QuoteStoreState extends ResourceContainer<Quote | null> {
  inventoryContractGenerationFailed: boolean;
  migrationErrors: Ref<string[]>;
  loadedQuoteType: QUOTE_TYPE | null;
  dirtyVersicherungsangabenFields: string[];
}

export const useQuoteStore = defineStore('QuoteStore', {
  state: (): QuoteStoreState => ({
    data: null,
    isLoading: false,
    inventoryContractGenerationFailed: false,
    migrationErrors: ref<string[]>([]),
    dirtyVersicherungsangabenFields: [],
    loadedQuoteType: null,
  }),

  actions: {
    buildContent(
      versicherungsangabenSections?: SECTION[],
      versicherungsangabenHelpInput?: string,
      versicherungsangabenDirtyFields?: VersicherungsangabenDirtyFields,
    ): QuoteContent {
      const tariffStore = useTariffStore();

      let versicherungsangaben: QuoteContent['versicherungsangaben'];

      if (
        versicherungsangabenSections !== undefined &&
        versicherungsangabenSections.length > 0
      ) {
        versicherungsangaben = {
          sections: versicherungsangabenSections,
          helpInput: versicherungsangabenHelpInput ?? '',
          dirtyFields: versicherungsangabenDirtyFields ?? {},
        };
      }

      return {
        version: QuoteVersion,
        searchData: useGlobalStore().getQuoteSearchData,
        contractData: useContractStore().$state,
        companyQuestionData: useCompanyQuestionStore().inputState,
        clients: useClientStore().entities,
        comparison: {
          recommendation: tariffStore.recommendedTariff,
          selection: tariffStore.selectedTariffs,
        },
        filters: {
          directAgreement: tariffStore.filters.directAgreement,
          search: tariffStore.localFilters.search,
          sorting: tariffStore.sorting,
          blacklistedCompanies: useCompaniesStore().blacklistedList,
        },
        profileId: useProfileStore().selected?.id ?? undefined,
        versicherungsangaben: versicherungsangaben,
      };
    },

    async loadQuoteById(
      id: string,
      type: QUOTE_TYPE = QUOTE_TYPE.DEFAULT,
      noLoadingState = false,
    ) {
      try {
        if (this.data !== null && this.data.id === Number(id)) {
          return this.data;
        }

        const quotesApi = useApi().quotes;
        this.isLoading = !noLoadingState;
        const [, request] = quotesApi.loadQuoteById(id);
        const data = await request;
        this.migrationErrors = [];

        if (data === null) {
          throw new VglError(VGL_ERROR_CODE.GENERAL_UNEXPECTED_EMPTY_RESPONSE, {
            additionalErrorMessage: 'quotes -> loadQuoteById',
          });
        }

        const immutableApiContent = Object.freeze(data.content);

        const contractStore = useContractStore();
        this.loadedQuoteType = type;

        if (
          data.content.version === undefined ||
          data.content.version < LegacyQuoteVersion
        ) {
          data.content = transformLegacyQuoteToCurrentVersion(
            data.content as unknown as LegacyQuoteContent,
          );

          if (isLegacyInventoryContractContent(immutableApiContent)) {
            contractStore.$patch(
              transformInventoryContractToCurrentVersion(
                immutableApiContent.antrag,
              ) as Partial<typeof contractStore.$state>,
            );
          }
        } else if (hasContractInputData(data.content)) {
          transferContractDataToContractStore(data.content.contractData);
        }

        if (data.content.searchData.migrationErrors !== undefined) {
          this.migrationErrors = data.content.searchData.migrationErrors;
        }

        if (data.content.profileId !== undefined) {
          try {
            await useProfileStore().loadSelectedProfile(data.content.profileId);
          } catch (error) {
            console.warn(error);
          }
        }

        const featuresStore = useFeaturesStore();

        data.content.searchData.merkmale.forEach((feature) => {
          try {
            featuresStore.setFeatureByIdentifier(
              feature.identifier,
              feature.value,
            );
          } catch {
            /* empty */
          }
        });

        transferQuoteDataToInputStore(data.content.searchData.eingabedaten);

        if (hasCompanyQuestionInputData(data.content)) {
          useCompanyQuestionStore().inputState =
            data.content.companyQuestionData;
        }

        if (data.content.searchData.allTimeTariff !== undefined) {
          transferQuoteAllTimeTariffsDataToInputStore(
            data.content.searchData.allTimeTariff,
          );
        }

        const tariffStore = useTariffStore();

        if (data.content.comparison.recommendation) {
          tariffStore.setRecommendation(
            data.content.comparison.recommendation.identifier,
          );
        }

        data.content.comparison.selection.forEach(({ identifier }) => {
          tariffStore.addToSelection(identifier);
        });

        tariffStore.filters.directAgreement =
          data.content.filters.directAgreement;

        tariffStore.localFilters.search = data.content.filters.search;
        tariffStore.sorting =
          data.content.filters.sorting.length === 0
            ? createDefaultSortingState()
            : data.content.filters.sorting;

        if (data.content.filters.blacklistedCompanies) {
          useCompaniesStore()
            .setBlacklist(data.content.filters.blacklistedCompanies)
            .then(() => {});
        }

        let { clients } = data.content;
        if (data.content.version !== QuoteVersion) {
          clients = transformClientEntitiesToEntityManagerStructure(
            data.content
              .clients as unknown as LegacyClientResourceContainerQuoteStructure[],
          );
        }

        useClientStore().loadClientsFromQuoteEntities(
          clients,
          true,
          data.content.versicherungsangaben !== undefined,
        );

        const config = getCurrentSpartenConfig();

        if (config.quotePostLoadCallback !== undefined) {
          config.quotePostLoadCallback(data.content);
        }

        if (data.content.versicherungsangaben?.dirtyFields !== undefined) {
          this.dirtyVersicherungsangabenFields = Object.keys(
            data.content.versicherungsangaben.dirtyFields,
          );
        }

        this.data = QUOTE_RESET_AFTER_LOAD.includes(type) ? null : data;

        return data;
      } catch (error) {
        if (
          error instanceof VglError &&
          error.getErrorCode() !== VGL_ERROR_CODE.API_AXIOS_ERROR // API errors most likely mean that the quote could not be loaded (will be handled in the next step)
        ) {
          throw error;
        }

        throw new VglError(
          type === QUOTE_TYPE.DEFAULT
            ? VGL_ERROR_CODE.QUOTE_LOADING_FAILED
            : VGL_ERROR_CODE.QUOTE_INVENTORY_CONTRACT_LOADING_FAILED,
          {
            additionalErrorMessage: `Error: ${getErrorMessage(
              error,
            )} - Id: ${id} - Type: ${type}`,
            cause: error,
          },
        );
      } finally {
        this.isLoading = false;
      }
    },

    async loadWebreportById(id: string, type: WEBREPORT_TYPE) {
      try {
        const quotesApi = useApi().quotes;
        this.isLoading = true;
        const [, request] = quotesApi.loadWebreportById(id);
        const response = await request;

        if (response === null) {
          throw new VglError(VGL_ERROR_CODE.GENERAL_UNEXPECTED_EMPTY_RESPONSE, {
            additionalErrorMessage: 'quotes -> loadWebreportById',
          });
        }

        if (response.data.userKonfiguration?.userSettings) {
          useBrokerStore().settings.data = {
            ...useBrokerStore().settings.data,
            ...response.data.userKonfiguration.userSettings,
          };
        }

        const tariffStore = useTariffStore();

        tariffStore.sorting = createDefaultSortingState();

        tariffStore.filters.directAgreement =
          response.data.vorgaben.konfigurationsParameter.direktvereinbarung;

        tariffStore.tariffChunks['0'] = {
          tariffs: response.data.tarife,
          tariffErrors: [],
        };

        if (response.data.userKonfiguration?.userSettings !== undefined) {
          const settings = {
            ...settingsSchema.getDefault(),
            ...response.data.userKonfiguration.userSettings,
          };

          if (settingsSchema.isValidSync(settings)) {
            useBrokerStore().settings.data = settings;
          }
        }

        if (response.data.allTimeTariffs) {
          tariffStore.allTimeTariffs = Array.isArray(
            response.data.allTimeTariffs,
          )
            ? response.data.allTimeTariffs
            : [response.data.allTimeTariffs];
        }

        transferQuoteDataToInputStore(response.data.vorgaben.eingabedaten);

        useClientStore().loadClientsFromQuoteEntities(
          Object.values(
            response.data.vorgaben.entities,
          ) as ClientResourceContainer[],
          true,
          true,
        );

        if (type === WEBREPORT_TYPE.COMPARISON) {
          tariffStore.allTariffs.forEach((tariff) => {
            tariffStore.toggleSelection(tariff);

            if (
              response.data.recommendation !== undefined &&
              response.data.recommendation === tariff.identifier
            ) {
              tariffStore.toggleRecommendation(tariff);
            }
          });

          const featuresStore = useFeaturesStore();

          if (response.data.merkmale !== undefined) {
            featuresStore.data = response.data.merkmale;

            response.data.vorgaben.merkmale.forEach((feature) => {
              try {
                featuresStore.setFeatureByIdentifier(
                  feature.identifier,
                  feature.value,
                );
              } catch {
                /* empty */
              }
            });

            response.data.tarife.forEach((tariff) => {
              const { merkmale } = tariff;
              const mappedByMerkmalKey: TariffFeatures = {};

              if (merkmale === undefined) return;

              merkmale.forEach((merkmal) => {
                mappedByMerkmalKey[merkmal.key] = merkmal;
              });
              featuresStore.tariffFeatures[tariff.identifier] =
                mappedByMerkmalKey;
            });
          }
        }
      } catch (error) {
        if (error instanceof VglError) {
          throw error;
        }

        throw new VglError(VGL_ERROR_CODE.QUOTE_WEBREPORT_LOADING_FAILED, {
          additionalErrorMessage: `Error: ${getErrorMessage(
            error,
          )} - Id: ${id}`,
          cause: error,
        });
      } finally {
        this.isLoading = false;
      }
    },

    getStorableQuoteContent(
      selectedRisikodatenSections?: SECTION[],
      versicherungsangabenHelpInput?: string,
      versicherungsangabenDirtyFields?: VersicherungsangabenDirtyFields,
    ) {
      let content = this.buildContent(
        selectedRisikodatenSections,
        versicherungsangabenHelpInput,
        versicherungsangabenDirtyFields,
      );

      const config = getCurrentSpartenConfig();

      if (config.quotePreStoreCallback !== undefined) {
        content = config.quotePreStoreCallback(content);
      }

      return content;
    },

    async storeQuote(overwrite: boolean, title: string, clientId: number) {
      try {
        const quotesApi = useApi().quotes;

        const quoteId: string | null = this.data?.id.toString() ?? null;

        const content = this.getStorableQuoteContent();

        let result;
        if (quoteId !== null && overwrite) {
          const [, request] = quotesApi.updateQuote(quoteId, title, content);
          result = await request;
        } else {
          const [, request] = quotesApi.saveQuote(clientId, title, content);
          result = await request;
        }

        if (result === null) {
          const storeType = overwrite ? 'update' : 'save';
          throw new VglError(VGL_ERROR_CODE.GENERAL_UNEXPECTED_EMPTY_RESPONSE, {
            additionalErrorMessage: `quotes -> ${quoteId} ${storeType}`,
          });
        }

        this.data = {
          id: result.id,
          clientId: result.clientId,
          title,
          content,
        };
      } catch (error) {
        throw new VglError(VGL_ERROR_CODE.QUOTE_STORING_FAILED, {
          additionalErrorMessage: `Error: ${getErrorMessage(
            error,
          )} - Overwrite: ${
            overwrite ? 'yes' : 'no'
          } - Title: ${title} - ClientId: ${clientId}`,
          cause: error,
        });
      }
    },

    async generateInventoryContract(
      data: InventoryContract,
      calculationId: string,
      contractId: number | null,
    ): Promise<void> {
      try {
        this.inventoryContractGenerationFailed = false;
        const api = useApi().quotes;

        const [, result] = api.generateInventoryContract(
          data,
          calculationId,
          contractId,
        );
        await result;
      } catch (error) {
        this.inventoryContractGenerationFailed = true;

        if (error instanceof VglError) {
          throw error;
        }

        throw new VglError(
          VGL_ERROR_CODE.QUOTE_GENERATE_INVENTORY_CONTRACT_FAILED,
          {
            additionalErrorMessage: `Error: ${getErrorMessage(error)}`,
            cause: error,
          },
        );
      }
    },
  },

  getters: {
    searchDataChanged(state) {
      if (state.data === undefined || state.data === null) {
        return false;
      }

      const searchData: SearchDataComparable = {
        eingabedaten: useStorableState().create(
          buildMergedInputData(state.data.content.searchData.eingabedaten),
        ),
        entities: transformEntitiesToComparableStructure(
          state.data.content.searchData.entities,
        ),
        merkmale: state.data.content.searchData.merkmale,
        blacklistedCompaniesHash:
          state.data.content.searchData.blacklistedCompaniesHash,
      };

      const tariffSearchData = useGlobalStore().getTariffSearchData();

      const searchDataCurrent: SearchDataComparable = {
        eingabedaten: tariffSearchData.eingabedaten,
        entities: transformEntitiesToComparableStructure(
          tariffSearchData.entities,
        ),
        merkmale: tariffSearchData.merkmale,
        blacklistedCompaniesHash: tariffSearchData.blacklistedCompaniesHash,
      };

      const quoteHashSearchDataHash = hashCode(
        Object.freeze(removeNullAndUndefinedValues(searchData)),
      );
      const tariffSearchHash = hashCode(
        removeNullAndUndefinedValues(searchDataCurrent),
      );

      return tariffSearchHash !== quoteHashSearchDataHash;
    },
  },
});
